import React, { useState, useEffect } from 'react';
import { Package, Plus, Edit2, Trash2, Loader2, Check, X, DollarSign, Tag, List, Star } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_TYPES = [
    { value: 'accounting', label: 'Accounting' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'combined', label: 'Combined' }
];

const AdminPackagesPage = () => {
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('combined');
    const [showDialog, setShowDialog] = useState(false);
    const [editingPackage, setEditingPackage] = useState(null);
    const [saving, setSaving] = useState(false);
    
    const [formData, setFormData] = useState({
        name: '',
        plan_type: 'combined',
        plan_tier: '',
        price: 0,
        description: '',
        features: [{ text: '', included: true }],
        is_popular: false,
        is_active: true,
        sort_order: 0
    });

    const fetchPackages = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('finmar_admin_token');
            const response = await fetch(`${API}/admin/packages`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
                const data = await response.json();
                setPackages(data.packages || []);
            }
        } catch (error) {
            toast.error('Failed to load packages');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPackages();
    }, []);

    const handleSave = async () => {
        if (!formData.name || !formData.plan_tier || formData.price <= 0) {
            toast.error('Please fill all required fields');
            return;
        }

        // Filter out empty features
        const features = formData.features.filter(f => f.text.trim());
        
        setSaving(true);
        try {
            const token = localStorage.getItem('finmar_admin_token');
            const url = editingPackage 
                ? `${API}/admin/packages/${editingPackage.package_id}`
                : `${API}/admin/packages`;
            
            const method = editingPackage ? 'PUT' : 'POST';
            
            const body = editingPackage ? {
                name: formData.name,
                price: parseFloat(formData.price),
                description: formData.description,
                features: features,
                is_popular: formData.is_popular,
                is_active: formData.is_active,
                sort_order: parseInt(formData.sort_order)
            } : {
                ...formData,
                price: parseFloat(formData.price),
                features: features,
                sort_order: parseInt(formData.sort_order)
            };

            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });

            if (response.ok) {
                toast.success(editingPackage ? 'Package updated' : 'Package created');
                setShowDialog(false);
                resetForm();
                fetchPackages();
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to save');
            }
        } catch (error) {
            toast.error('Failed to save package');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (pkg) => {
        if (!window.confirm(`Delete "${pkg.name}"? This may affect active subscriptions.`)) return;

        try {
            const token = localStorage.getItem('finmar_admin_token');
            const response = await fetch(`${API}/admin/packages/${pkg.package_id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const data = await response.json();
            toast.success(data.message);
            fetchPackages();
        } catch (error) {
            toast.error('Failed to delete package');
        }
    };

    const openEditDialog = (pkg) => {
        setEditingPackage(pkg);
        setFormData({
            name: pkg.name,
            plan_type: pkg.plan_type,
            plan_tier: pkg.plan_tier,
            price: pkg.price,
            description: pkg.description || '',
            features: pkg.features?.length ? pkg.features : [{ text: '', included: true }],
            is_popular: pkg.is_popular || false,
            is_active: pkg.is_active !== false,
            sort_order: pkg.sort_order || 0
        });
        setShowDialog(true);
    };

    const openCreateDialog = () => {
        resetForm();
        setFormData(prev => ({ ...prev, plan_type: activeTab }));
        setShowDialog(true);
    };

    const resetForm = () => {
        setEditingPackage(null);
        setFormData({
            name: '',
            plan_type: 'combined',
            plan_tier: '',
            price: 0,
            description: '',
            features: [{ text: '', included: true }],
            is_popular: false,
            is_active: true,
            sort_order: 0
        });
    };

    const addFeature = () => {
        setFormData(prev => ({
            ...prev,
            features: [...prev.features, { text: '', included: true }]
        }));
    };

    const updateFeature = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            features: prev.features.map((f, i) => 
                i === index ? { ...f, [field]: value } : f
            )
        }));
    };

    const removeFeature = (index) => {
        setFormData(prev => ({
            ...prev,
            features: prev.features.filter((_, i) => i !== index)
        }));
    };

    const filteredPackages = packages.filter(p => p.plan_type === activeTab);

    return (
        <div className="space-y-6" data-testid="admin-packages-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-finmar-navy">Pricing Packages</h1>
                    <p className="text-slate-500">Manage subscription packages and pricing</p>
                </div>
                <Button
                    onClick={openCreateDialog}
                    className="bg-finmar-gold hover:bg-finmar-gold/90 text-finmar-navy"
                    data-testid="create-package-btn"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Package
                </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-blue-100 rounded-lg">
                                <Package className="w-6 h-6 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-finmar-navy">{packages.length}</p>
                                <p className="text-sm text-slate-500">Total Packages</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-green-100 rounded-lg">
                                <Check className="w-6 h-6 text-green-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-finmar-navy">
                                    {packages.filter(p => p.is_active !== false).length}
                                </p>
                                <p className="text-sm text-slate-500">Active</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-amber-100 rounded-lg">
                                <Star className="w-6 h-6 text-amber-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-finmar-navy">
                                    {packages.filter(p => p.is_popular).length}
                                </p>
                                <p className="text-sm text-slate-500">Popular</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-slate-100 rounded-lg">
                                <X className="w-6 h-6 text-slate-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-finmar-navy">
                                    {packages.filter(p => p.is_active === false).length}
                                </p>
                                <p className="text-sm text-slate-500">Inactive</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Packages by Type */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="bg-slate-100">
                    <TabsTrigger value="combined">Combined</TabsTrigger>
                    <TabsTrigger value="accounting">Accounting</TabsTrigger>
                    <TabsTrigger value="marketing">Marketing</TabsTrigger>
                </TabsList>

                <TabsContent value={activeTab} className="mt-4">
                    <Card>
                        <CardHeader>
                            <CardTitle className="capitalize">{activeTab} Packages</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {loading ? (
                                <div className="flex items-center justify-center py-12">
                                    <Loader2 className="w-8 h-8 animate-spin text-finmar-gold" />
                                </div>
                            ) : filteredPackages.length === 0 ? (
                                <div className="text-center py-12">
                                    <Package className="w-16 h-16 mx-auto text-slate-200 mb-4" />
                                    <p className="text-slate-500">No packages in this category</p>
                                    <Button variant="outline" className="mt-4" onClick={openCreateDialog}>
                                        Create First Package
                                    </Button>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {filteredPackages.sort((a, b) => a.sort_order - b.sort_order).map(pkg => (
                                        <div
                                            key={pkg.package_id}
                                            className={`relative border rounded-lg p-4 ${pkg.is_active === false ? 'opacity-50 bg-slate-50' : ''} ${pkg.is_popular ? 'border-finmar-gold ring-1 ring-finmar-gold' : 'border-slate-200'}`}
                                        >
                                            {pkg.is_popular && (
                                                <span className="absolute -top-2 left-4 bg-finmar-gold text-finmar-navy text-xs font-semibold px-2 py-0.5 rounded">
                                                    Popular
                                                </span>
                                            )}
                                            
                                            <div className="flex items-start justify-between mb-3">
                                                <div>
                                                    <h3 className="font-semibold text-finmar-navy">{pkg.name}</h3>
                                                    <p className="text-xs text-slate-500 capitalize">{pkg.plan_tier}</p>
                                                </div>
                                                <span className={`px-2 py-0.5 rounded text-xs ${pkg.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                                                    {pkg.is_active !== false ? 'Active' : 'Inactive'}
                                                </span>
                                            </div>
                                            
                                            <div className="mb-3">
                                                <span className="text-2xl font-bold text-finmar-navy">${pkg.price}</span>
                                                <span className="text-slate-500">/{pkg.billing_period || 'month'}</span>
                                            </div>
                                            
                                            {pkg.features && pkg.features.length > 0 && (
                                                <ul className="space-y-1 mb-4 text-sm">
                                                    {pkg.features.slice(0, 4).map((f, i) => (
                                                        <li key={i} className="flex items-center gap-2 text-slate-600">
                                                            <Check className="w-3 h-3 text-green-500" />
                                                            <span className="truncate">{f.text}</span>
                                                        </li>
                                                    ))}
                                                    {pkg.features.length > 4 && (
                                                        <li className="text-slate-400 text-xs">+{pkg.features.length - 4} more features</li>
                                                    )}
                                                </ul>
                                            )}
                                            
                                            <div className="flex items-center gap-2 pt-3 border-t">
                                                <Button variant="outline" size="sm" className="flex-1" onClick={() => openEditDialog(pkg)}>
                                                    <Edit2 className="w-3 h-3 mr-1" />
                                                    Edit
                                                </Button>
                                                <Button variant="ghost" size="sm" onClick={() => handleDelete(pkg)} className="text-red-500 hover:text-red-700">
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Create/Edit Dialog */}
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Package className="w-5 h-5 text-finmar-gold" />
                            {editingPackage ? 'Edit Package' : 'Create Package'}
                        </DialogTitle>
                        <DialogDescription>
                            {editingPackage ? 'Update package details and pricing' : 'Add a new pricing package'}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Package Name <span className="text-red-500">*</span></Label>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="e.g., Growth Plan"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Price (AUD) <span className="text-red-500">*</span></Label>
                                <div className="relative">
                                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    <Input
                                        type="number"
                                        value={formData.price}
                                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                        className="pl-9"
                                        min="0"
                                        step="0.01"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Plan Type</Label>
                                <Select
                                    value={formData.plan_type}
                                    onValueChange={(v) => setFormData({ ...formData, plan_type: v })}
                                    disabled={!!editingPackage}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {PLAN_TYPES.map(t => (
                                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Plan Tier <span className="text-red-500">*</span></Label>
                                <Input
                                    value={formData.plan_tier}
                                    onChange={(e) => setFormData({ ...formData, plan_tier: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                                    placeholder="e.g., starter, growth, pro"
                                    disabled={!!editingPackage}
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Description</Label>
                            <Textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Brief description of this package..."
                                rows={2}
                            />
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label>Features</Label>
                                <Button type="button" variant="ghost" size="sm" onClick={addFeature}>
                                    <Plus className="w-4 h-4 mr-1" /> Add Feature
                                </Button>
                            </div>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                                {formData.features.map((feature, index) => (
                                    <div key={index} className="flex items-center gap-2">
                                        <Switch
                                            checked={feature.included}
                                            onCheckedChange={(v) => updateFeature(index, 'included', v)}
                                        />
                                        <Input
                                            value={feature.text}
                                            onChange={(e) => updateFeature(index, 'text', e.target.value)}
                                            placeholder="Feature description"
                                            className="flex-1"
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => removeFeature(index)}
                                            className="text-red-500"
                                        >
                                            <X className="w-4 h-4" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                <div>
                                    <p className="font-medium text-sm">Mark as Popular</p>
                                    <p className="text-xs text-slate-500">Highlight this package</p>
                                </div>
                                <Switch
                                    checked={formData.is_popular}
                                    onCheckedChange={(v) => setFormData({ ...formData, is_popular: v })}
                                />
                            </div>
                            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                <div>
                                    <p className="font-medium text-sm">Active</p>
                                    <p className="text-xs text-slate-500">Show on pricing page</p>
                                </div>
                                <Switch
                                    checked={formData.is_active}
                                    onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Sort Order</Label>
                            <Input
                                type="number"
                                value={formData.sort_order}
                                onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
                                min="0"
                                className="w-24"
                            />
                            <p className="text-xs text-slate-500">Lower numbers appear first</p>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                        <Button
                            onClick={handleSave}
                            disabled={saving}
                            className="bg-finmar-gold hover:bg-finmar-gold/90 text-finmar-navy"
                        >
                            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                            {editingPackage ? 'Save Changes' : 'Create Package'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminPackagesPage;
